import React from 'react';
import { Loader2 } from 'lucide-react';
import { moduleApi } from '../../../../lib/api';
import { Modal } from '../../../ui/Modal';
import { Field, Input } from '../../../ui/Field';
import { useConfirm } from '../../../ui/ConfirmProvider';
import { SURTITRE, pillPrimary, pillSecondary, pillDangerGhost } from '../../../ui/pills';
import type { AccesInfo, AccesOrganisateur, LigneJournalOrga } from './OrganisateurAccesCard';

const fmtDate = (iso?: string | null) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return '—'; } };
const fmtVisite = (iso?: string | null) => {
    if (!iso) return 'Jamais';
    try {
        const d = new Date(iso);
        const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        return d.toDateString() === new Date().toDateString() ? `Aujourd'hui à ${heure}` : `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à ${heure}`;
    } catch { return '—'; }
};
const fmtJournal = (iso: string) => { try { const d = new Date(iso); return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`; } catch { return ''; } };

const ETAT: Record<AccesOrganisateur['etat'], { label: string; cls: string }> = {
    ok: { label: 'Actif', cls: 'bg-state-ok-soft border-state-ok/30 text-state-ok' },
    expire: { label: 'Expiré', cls: 'bg-state-warn-soft border-state-warn/30 text-state-warn' },
    revoque: { label: 'Révoqué', cls: 'bg-surface-soft border-border-soft text-content-muted' },
};

/** 19d — la carte « Accès organisateur » : ce que l'établissement voit d'un accès (jamais le code, jamais la session). */
function CarteAcces({ a, occupe, onRenvoyer, onNouveauCode, onRevoquer }: { a: AccesOrganisateur; occupe: boolean; onRenvoyer: () => void; onNouveauCode: () => void; onRevoquer: () => void }) {
    const lignes: [string, React.ReactNode, boolean][] = [
        ['Organisateur', a.name, false],
        ['Adresse', a.email, true],
        ['Lien', `/organisateur/${a.token.slice(0, 4)}…${a.token.slice(-4)}`, true],
        ['Code', '•  •  •  •  •  •', true],
        ['Expire le', fmtDate(a.expires_at), false],
        ['Dernière visite', fmtVisite(a.last_seen_at), false],
    ];
    return (
        <div className="p-4 rounded-xl bg-surface border border-border">
            <div className="flex items-baseline justify-between gap-2.5 flex-wrap gap-y-1 mb-[13px]">
                <span className={SURTITRE}>Accès organisateur</span>
                <span className={`inline-flex items-center h-[22px] px-[9px] rounded-md border text-[10px] font-bold tracking-[.06em] uppercase whitespace-nowrap ${ETAT[a.etat].cls}`}>{ETAT[a.etat].label}</span>
            </div>
            <div className="flex flex-col">
                {lignes.map(([k, v, mono], i) => (
                    <div key={k} className={`flex items-baseline justify-between gap-3.5 py-2 ${i > 0 ? 'border-t border-border-soft' : ''}`}>
                        <span className="text-[12px] text-content-muted shrink-0">{k}</span>
                        <span className={`text-[12.5px] font-semibold text-content text-right min-w-0 truncate ${mono ? 'font-mono' : ''}`}>{v}</span>
                    </div>
                ))}
            </div>
            {a.etat !== 'revoque' && (
                <div className="flex gap-2 flex-wrap mt-3.5 pt-[13px] border-t border-border-soft">
                    <button type="button" onClick={onRenvoyer} disabled={occupe || a.etat !== 'ok'} className={pillSecondary(34)}>Renvoyer le lien</button>
                    <button type="button" onClick={onNouveauCode} disabled={occupe} className={pillSecondary(34)}>Nouveau code</button>
                    <button type="button" onClick={onRevoquer} disabled={occupe} className={pillDangerGhost(34)}>Révoquer</button>
                </div>
            )}
        </div>
    );
}

/** 19d — le journal : tout ce que fait l'organisateur, avec son nom ; et les gestes de l'établissement sur l'accès. */
function Journal({ lignes }: { lignes: LigneJournalOrga[] }) {
    return (
        <div className="p-4 rounded-xl bg-surface border border-border">
            <div className={`${SURTITRE} mb-3`}>Journal de l'organisateur</div>
            {lignes.length === 0 ? (
                <p className="text-[12px] text-content-muted">Rien pour l'instant : le journal commence à la création de l'accès.</p>
            ) : (
                <div className="flex flex-col max-h-[360px] overflow-y-auto">
                    {lignes.map((j, i) => (
                        <div key={i} className={`flex gap-[11px] py-[9px] ${i > 0 ? 'border-t border-border-soft' : ''}`}>
                            <span className="font-mono text-[10.5px] text-content-muted shrink-0 w-[66px] tabular-nums">{fmtJournal(j.created_at)}</span>
                            <div className="min-w-0">
                                <div className="text-[12px] leading-[1.45] text-content text-pretty">{j.text}</div>
                                <div className="mt-px text-[10.5px] text-content-muted">{j.actor_label} · {j.source === 'portail' ? 'portail' : 'établissement'}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <p className="mt-[13px] pt-3 border-t border-border-soft text-[11.5px] leading-[1.5] text-content-muted text-pretty">Tout ce que fait l'organisateur est consigné ici, avec son nom. Rien ne se produit dans le dossier sans trace.</p>
        </div>
    );
}

/**
 * « Gérer l'accès » (maquettes 18b → 19d) : donner l'accès (prérempli depuis la fiche, jamais imposé, deux
 * organisateurs au plus), le renvoyer, renouveler le code, le révoquer — et lire le journal.
 */
export function OrganisateurAccesModal({ eventId, info, onChange, onClose }: { eventId: string; info: AccesInfo | null; onChange: () => void; onClose: () => void }) {
    const confirmAction = useConfirm();
    const [name, setName] = React.useState(info?.contact.name || '');
    const [email, setEmail] = React.useState(info?.contact.email || '');
    const [occupe, setOccupe] = React.useState(false);
    const [message, setMessage] = React.useState<{ ton: 'ok' | 'erreur'; texte: string } | null>(null);
    const acces = (info?.acces || []).filter((a) => a.etat !== 'revoque');
    const peutCreer = acces.filter((a) => a.etat === 'ok').length < (info?.max || 2);

    const lancer = async (fn: () => Promise<any>, ok: string) => {
        setOccupe(true); setMessage(null);
        try {
            const r: any = await fn();
            if (r && r.success === false) setMessage({ ton: 'erreur', texte: r.error || 'Le courriel n’est pas parti.' });
            else if (r && r.email_sent === false) setMessage({ ton: 'erreur', texte: `Accès créé, mais le courriel n'est pas parti : ${r.email_error || 'erreur inconnue'}.` });
            else setMessage({ ton: 'ok', texte: ok });
            onChange();
        } catch (e: any) { setMessage({ ton: 'erreur', texte: e?.message || 'Erreur.' }); } finally { setOccupe(false); }
    };

    return (
        <Modal open onClose={onClose} size="2xl" title="Accès organisateur" subtitle="Le client importe, envoie et relance lui-même — sur un lien et un code provisoires, sans voir ni tarif ni contrainte nominative."
            footer={<button type="button" onClick={onClose} className={pillSecondary()}>Fermer</button>}>
            {!info ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div> : (
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,420fr)_minmax(0,460fr)] gap-4 items-start">
                    <div className="flex flex-col gap-3">
                        {acces.map((a) => (
                            <CarteAcces key={a.id} a={a} occupe={occupe}
                                onRenvoyer={() => lancer(() => moduleApi.renvoyerOrganisateurLien(eventId, a.id), `Lien renvoyé à ${a.email}. Le code reste celui déjà reçu.`)}
                                onNouveauCode={() => lancer(() => moduleApi.nouveauCodeOrganisateur(eventId, a.id), `Nouveau code envoyé à ${a.email}. L'ancien code et la session en cours ne fonctionnent plus.`)}
                                onRevoquer={async () => {
                                    if (!(await confirmAction({ title: "Révoquer l'accès ?", description: <><strong>{a.name}</strong> ne pourra plus ouvrir le portail. Ce qu'il a déjà fait reste dans le dossier et dans le journal.</>, confirmLabel: "Révoquer l'accès" }))) return;
                                    lancer(() => moduleApi.revoquerOrganisateurAcces(eventId, a.id), `Accès de ${a.name} révoqué.`);
                                }} />
                        ))}
                        {peutCreer && (
                            <div className="p-4 rounded-xl bg-surface border border-border">
                                <div className={`${SURTITRE} mb-3`}>{acces.length ? 'Second organisateur' : "Donner l'accès"}</div>
                                <div className="space-y-3">
                                    <Field label="Organisateur">{({ id }) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom et nom" />}</Field>
                                    <Field label="Adresse électronique" help="Le lien et le code partent à cette adresse, à votre en-tête.">
                                        {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="organisateur@exemple.fr" />}
                                    </Field>
                                    <button type="button" disabled={occupe || !name.trim() || !email.trim()} className={pillPrimary(34)}
                                        onClick={async () => { const a = email.trim(); await lancer(() => moduleApi.createOrganisateurAcces(eventId, { name: name.trim(), email: a }), `Accès créé et envoyé à ${a}.`); setName(''); setEmail(''); }}>
                                        {occupe ? <Loader2 size={13} className="animate-spin" /> : null} Créer l'accès et envoyer le lien
                                    </button>
                                </div>
                                <p className="mt-2.5 text-[11.5px] leading-[1.5] text-content-muted text-pretty">Deux organisateurs au plus, chacun son lien et son code, chacun tracé séparément. L'accès expire trois jours après l'événement.</p>
                            </div>
                        )}
                        {message && <p className={`text-[12px] font-semibold ${message.ton === 'ok' ? 'text-state-ok' : 'text-state-danger'}`}>{message.texte}</p>}
                    </div>
                    <Journal lignes={info.journal} />
                </div>
            )}
        </Modal>
    );
}
